import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Select from "../../../sub-component/Select";
import Input from "../../../sub-component/Input";
import { ReactComponent as Search } from "../../../../assets/icons/Views/candidate/assessments/search.svg";
import CustomDropdown from "../../../common/CustomDropdown";
import { listAssessSpec } from "../../../../redux/thunks/AssessSpec";
import {
  ConnectedApps,
  GetAllStages,
  GetJobs,
  GetSettings,
  IntegrateSettings,
  UpdateAPiKey,
} from "../../../../redux/thunks/Integration";
import Button from "../../../sub-component/Button";
import CustomTooltip from "../../../common/CustomTooltip";
import { ReactComponent as InfoIcon } from "../../../../assets/icons/Views/dashboard/assessments/info-1.svg";
import { Box, Modal } from "@mui/material";
import AlertBox from "../../../common/AlertBox";
import { ReactComponent as ErrorIcon } from "../../../../assets/icons/basic/x-circle-error.svg";
import UnlinkIcon from "../../../../assets/icons/basic/unlink.png";
import NewSelect from "../../../sub-component/NewSelect";
import CustomSelect from "../../../sub-component/NewSelect";

const PAGE_SIZE = 10; // Number of jobs/assessments to fetch per page

const IntegrationSetting = ({
  appId,
  selectedIntegration,
  connectedAppData,
  connectedAppId,
  setConnectedAppData,
}) => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.authReducer);
  const { APIKEY } = useSelector((state) => state.integrationReducer);
  const assessSpecReducer = useSelector((state) => state.assessSpecReducer);
  const [assessments, setAssessments] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [apiKey, setApiKey] = useState(APIKEY);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedSpecId, setSelectedSpecId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [jobMappings, setJobMappings] = useState([]);
  const [savedAccessToken, setSavedAccessToken] = useState(null);
  const [alert, setAlert] = useState(null);
  const [unlinkProcess, setUnlinkProcess] = useState(null);
  const [selectedSpecIds, setSelectedSpecIds] = useState({});
  const [jobRole, setJobRole] = useState([]);
  const [jobDimensions, setJobDimensions] = useState({});
  const [isUpdated, setIsUpdated] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsHasMore, setJobsHasMore] = useState(true);
  const [assessmentsPage, setAssessmentsPage] = useState(1);
  const [assessmentsHasMore, setAssessmentsHasMore] = useState(true);
  const [stages, setStages] = useState([]);
  const [stagesLoaded, setStagesLoaded] = useState(false);
  const [stagesLoading, setStagesLoading] = useState(true); // Start with loading true
  const [jobsLoaded, setJobsLoaded] = useState(false);
  const [jobsLoading, setJobsLoading] = useState(true); // Start with loading true

  const [apiError, setApiError] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(false);
  const [jobList, setJobList] = useState(() =>
    jobs.map((job) => ({ ...job, isLinked: true })),
  );

  // Lazy load jobs
  const fetchJobs = async (page = 1) => {
    if (auth?.data?.access_token) {
      try {
        setJobsLoading(true);
        setJobsLoaded(false); // Reset loaded state when fetching

        const response = await dispatch(
          GetJobs({ offset: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE }),
        );

        // Check if the request was fulfilled
        if (response?.meta?.requestStatus === "fulfilled") {
          if (response?.payload?.data) {
            if (page === 1) {
              setJobs(response.payload.data);
            } else {
              setJobs((prev) => [...prev, ...response.payload.data]);
            }
            setJobsHasMore(response.payload.data.length === PAGE_SIZE);
            setJobsLoaded(true); // Mark jobs as loaded successfully
          } else {
            setJobsHasMore(false);
            setJobsLoaded(true); // Mark as loaded even if no data
          }
        } else {
          setJobsHasMore(false);
          setJobsLoaded(true); // Mark as loaded even on error to prevent infinite loading
          console.error("Jobs sync failed:", response?.error);
        }
      } catch (error) {
        setJobsHasMore(false);
        setJobsLoaded(true); // Mark as loaded even on error to prevent infinite loading
        console.error("Error fetching jobs:", error);
      } finally {
        setJobsLoading(false);
      }
    }
  };

  // Lazy load assessments
  const fetchAssessments = async (page = 1) => {
    try {
      const result = await dispatch(
        listAssessSpec({
          query: `?limit=${PAGE_SIZE}&offset=${(page - 1) * PAGE_SIZE}`,
        }),
      );
      const activeAssessments =
        result?.payload?.results
          ?.filter((assessment) => assessment.status === "ACTIVE")
          ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) ||
        [];
      if (page === 1) {
        setAssessments(activeAssessments);
      } else {
        setAssessments((prev) => [...prev, ...activeAssessments]);
      }
      setAssessmentsHasMore(activeAssessments.length === PAGE_SIZE);
    } catch (error) {
      setAssessmentsHasMore(false);
      console.error("Error fetching assessment specs:", error);
    }
  };

  // Fetch stages
  const fetchStages = async () => {
    try {
      setStagesLoading(true);
      setStagesLoaded(false); // Reset loaded state when fetching
      const response = await dispatch(GetAllStages());

      let stagesData = [];

      // Check if the request was fulfilled
      if (response?.meta?.requestStatus === "fulfilled") {
        // Handle different possible response structures
        if (response?.payload?.data) {
          stagesData = Array.isArray(response.payload.data)
            ? response.payload.data
            : [];
        } else if (response?.payload) {
          // Handle case where data might be directly in payload
          stagesData = Array.isArray(response.payload) ? response.payload : [];
        } else if (response?.data) {
          // Handle case where data might be directly in response
          stagesData = Array.isArray(response.data) ? response.data : [];
        }

        // Ensure all stages have the required name property
        stagesData = stagesData.filter(
          (stage) => stage && typeof stage === "object" && stage.name,
        );
        setStagesLoaded(true); // Mark stages as loaded successfully
      } else {
        console.error("Stages sync failed:", response?.error);
        setStagesLoaded(true); // Mark as loaded even on error to prevent infinite loading
      }

      setStages(stagesData);
    } catch (error) {
      console.error("Error fetching stages:", error);
      setStages([]);
      setStagesLoaded(true); // Set to true even on error to prevent infinite loading
    } finally {
      setStagesLoading(false);
    }
  };

  useEffect(() => {
    // Fetch jobs with sync first, then get API
    fetchJobs(1);
    setJobsPage(1);
  }, [dispatch, auth?.data?.access_token]);

  useEffect(() => {
    fetchAssessments(1);
    setAssessmentsPage(1);
  }, [dispatch]);

  useEffect(() => {
    // Fetch stages with sync first, then get API
    if (auth?.data?.access_token) {
      fetchStages();
    }
  }, [dispatch, auth?.data?.access_token]);

  const handleUnlink = async (jobId) => {
    setUnlinkProcess(jobId);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const updatedJobMappings = jobMappings.filter(
        (job) => job.job_id !== jobId,
      );
      setJobMappings(updatedJobMappings);

      // Ensure that the job's isLinked status is updated
      const updatedJobs = jobs.map((job) =>
        job.id === jobId ? { ...job, isLinked: false } : job,
      );
      setJobs(updatedJobs);

      // Clear the selected spec ID for this job
      setSelectedSpecIds((prev) => {
        const updated = { ...prev };
        delete updated[jobId];
        return updated;
      });

      await triggerIntegrationSettingAPI({ job_mappings: updatedJobMappings });
      setRefreshTrigger((prev) => !prev);

      setAlert(
        <AlertBox
          style={{ zIndex: "99999" }}
          color="success"
          description={"Assessment unlinked!"}
          title="Success"
          isOpen={true}
          onCloseAlert={() => setAlert(null)}
        />,
      );
    } catch (error) {
      console.error("Error unlinking job:", error);
    } finally {
      setUnlinkProcess(null);
    }
  };

  const filteredJobs = jobs.filter((job) =>
    job.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const [selectedStages, setSelectedStages] = useState({
    assess_invitation_stage: "",
    post_assess_stage_shortlisted: "",
    post_assess_stage_rejected: "",
    reject_on_assessment_failure: false,
  });
  const [directlyReject, setDirectlyReject] = useState(false);

  const hideApiKey = (key) => {
    if (!key) return "";
    return key.length > 5
      ? `${key.slice(0, 3)}****************${key.slice(-3)}`
      : key;
  };
  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await dispatch(listAssessSpec({ query: "?limit=100" }));
        const activeAssessments =
          result?.payload?.results
            ?.filter((assessment) => assessment.status === "ACTIVE")
            ?.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) ||
          [];

        setAssessments(activeAssessments);
      } catch (error) {
        console.error("Error fetching assessment specs:", error);
      }
    };

    fetchData();
  }, [dispatch]);

  const handleChange = (e) => {
    setConnectedAppData((prevData) => ({
      ...prevData,
      access_token: e.target.value,
    }));
  };
  const handleSave = async () => {
    setApiError("");

    if (connectedAppData?.access_token !== apiKey) {
      try {
        setIsSaving(true);
        setProcessing(true);
        setJobsLoading(true); // Start loading jobs
        setStagesLoading(true); // Start loading stages

        const apiKeyUpdated = await dispatch(
          UpdateAPiKey(connectedAppData?.access_token),
        ).unwrap();

        if (apiKeyUpdated) {
          // Trigger sync and get jobs after API key update
          setAlert(
            <AlertBox
              style={{ zIndex: "99999" }}
              color="info"
              description={"Syncing jobs and stages with Workable..."}
              title="Syncing"
              isOpen={true}
              onCloseAlert={() => setAlert(null)}
            />,
          );

          // Fetch jobs and stages in parallel
          const [jobResponse, stagesResponse] = await Promise.all([
            dispatch(GetJobs()),
            dispatch(GetAllStages()),
          ]);

          // Handle jobs response
          if (
            jobResponse?.meta?.requestStatus === "fulfilled" &&
            jobResponse?.payload?.data
          ) {
            setJobs(jobResponse.payload.data);
            setJobsLoaded(true);
          } else {
            setJobsLoaded(true); // Mark as loaded even on error
          }

          // Handle stages response
          if (stagesResponse?.meta?.requestStatus === "fulfilled") {
            let stagesData = [];

            if (stagesResponse?.payload?.data) {
              stagesData = Array.isArray(stagesResponse.payload.data)
                ? stagesResponse.payload.data
                : [];
            } else if (stagesResponse?.payload) {
              stagesData = Array.isArray(stagesResponse.payload)
                ? stagesResponse.payload
                : [];
            } else if (stagesResponse?.data) {
              stagesData = Array.isArray(stagesResponse.data)
                ? stagesResponse.data
                : [];
            }

            stagesData = stagesData.filter(
              (stage) => stage && typeof stage === "object" && stage.name,
            );
            setStages(stagesData);
            setStagesLoaded(true);
          } else {
            setStagesLoaded(true); // Mark as loaded even on error
          }

          if (connectedAppId) {
            const response = await dispatch(
              ConnectedApps({ id: connectedAppId }),
            );

            if (response?.payload?.access_token) {
              setSavedAccessToken(response.payload.access_token);
              setAlert(
                <AlertBox
                  style={{ zIndex: "99999" }}
                  color="success"
                  description={"API Key updated successfully!"}
                  title="Success"
                  isOpen={true}
                  onCloseAlert={() => setAlert(null)}
                />,
              );
            }
          } else {
            console.warn("Connected App ID not available.");
          }
        }

        setModalOpen(false);
      } catch (error) {
        console.log("Error received:", error);

        // Check if the error message is exactly "Unauthorized"
        if (error === "Unauthorized" || error?.message === "Unauthorized") {
          console.log("Setting error to: Incorrect API Key");
          setApiError("incorrect api key");
        } else {
          // Show whatever message comes from the backend
          const errorMessage = (
            typeof error === "string"
              ? error
              : error?.message || "failed to update api key"
          ).toLowerCase();
          console.log("Setting error to:", errorMessage);
          setApiError(errorMessage);
        }
      } finally {
        setIsSaving(false);
        setProcessing(false);
        setJobsLoading(false); // Stop loading jobs
        setStagesLoading(false); // Stop loading stages
      }
    } else {
      setApiError("API Key is the same. No changes made.");
    }
  };

  const triggerIntegrationSettingAPI = async (updatedData = {}) => {
    const finalAssessmentSettings =
      updatedData.assessment_settings ?? selectedStages;
    const finalJobMappings = updatedData.job_mappings ?? jobMappings;

    const payload = {
      app_id: appId,
      assessment_settings: finalAssessmentSettings,
      job_mappings: finalJobMappings,
      auth_token: auth?.data?.access_token,
    };

    try {
      // Only proceed if there are changes
      if (
        finalAssessmentSettings !== selectedStages ||
        finalJobMappings !== jobMappings
      ) {
        const response = await dispatch(IntegrateSettings(payload)).unwrap();

        let alertMessage = "Integration setting updated!";
        if (updatedData.job_mappings) {
          alertMessage = "Assessment linked to Job";
        } else if (updatedData.assessment_settings) {
          alertMessage = "Integration setting updated!";
        }

        setAlert(
          <AlertBox
            style={{ zIndex: "99999" }}
            color="success"
            description={alertMessage}
            title="Success"
            isOpen={true}
            onCloseAlert={() => setAlert(null)}
          />,
        );
        setIsUpdated(true); // Indicate the update occurred
      }
    } catch (error) {
      console.error("API failed", error);
    }
  };

  const handleUpdateHiringStage = (key, value) => {
    const updatedStages = { ...selectedStages, [key]: value };
    setSelectedStages(updatedStages);

    if (selectedSpecId) {
      triggerIntegrationSettingAPI({ assessment_settings: updatedStages });
    } else {
      triggerIntegrationSettingAPI({ assessment_settings: updatedStages });
    }
  };

  useEffect(() => {
    if (selectedSpecId) {
      triggerIntegrationSettingAPI({ assessment_settings: selectedStages });
    }
  }, [selectedStages]);

  const handleUpdateJobRole = (jobId, specId) => {
    setJobMappings((prevMappings) => {
      const updatedMappings = prevMappings.some(
        (mapping) => mapping.job_id === jobId,
      )
        ? prevMappings.map((mapping) =>
            mapping.job_id === jobId
              ? { ...mapping, spec_id: specId }
              : mapping,
          )
        : [...prevMappings, { job_id: jobId, spec_id: specId }];

      if (specId) {
        triggerIntegrationSettingAPI({ job_mappings: updatedMappings });
      } else {
        console.warn("Spec ID is null, API will not be called");
      }

      return updatedMappings;
    });
  };

  useEffect(() => {
    const newSelectedSpecIds = {};
    jobMappings.forEach((mapping) => {
      newSelectedSpecIds[mapping.job_id] = mapping.spec_id;
    });
    setSelectedSpecIds(newSelectedSpecIds);
  }, [jobMappings]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const getSettings = await dispatch(GetSettings({ app_id: appId }));
        const data = getSettings?.payload;

        if (data?.assessment_settings) {
          setSelectedStages((prev) => ({
            ...prev,
            ...data.assessment_settings,
          }));
          // Check if reject_on_assessment_failure is true
          if (data.assessment_settings.reject_on_assessment_failure === true) {
            setDirectlyReject(true);
          } else {
            setDirectlyReject(false);
          }
        }

        if (data?.job_mappings) {
          setJobMappings(data.job_mappings);
          // console.log(data?.job_mappings, "data?.job_mappings");
        }

        const specId = data?.job_mappings?.[0]?.spec_id;
        if (specId) {
          setSelectedSpecId(specId);
        }
      } catch (error) {
        console.error("Error fetching assessment specs:", error);
      } finally {
        setLoading(false);
      }
    };
    if (appId && jobsLoaded && stagesLoaded) {
      fetchData();
    }
  }, [appId, dispatch, refreshTrigger, jobsLoaded, stagesLoaded]);

  const settings = useSelector(
    (state) => state.integrationReducer.settings?.job_mappings,
  );
  const AssessmentSettings = useSelector(
    (state) => state.integrationReducer.settings?.assessment_settings,
  );

  const selectedPostAssessStage =
    selectedStages.post_assess_stage_shortlisted || "";

  // if (loading ) {
  //   return (
  //     <div style={{ height: "80vh" }}>
  //       <div className="processing-indicator-wrapper medium-indicator w-100 h-100 d-flex align-items-center justify-content-center">
  //         <div
  //           className="processing-indicator primary"
  //           style={{ width: "40px", height: "40px" }}
  //         />
  //       </div>
  //     </div>
  //   );
  // }

  return (
    <>
      {alert}
      <div>
        <div
          className="d-flex"
          style={{ flexDirection: "column", gap: "24px" }}
        >
          <div className="d-flex flex-column mt-4">
            <label
              className="body-2 dark-50 mb-2 w-100 text-start"
              style={{ marginLeft: "24px" }}
            >
              API Key
            </label>
            <div className="d-flex api-key-section" style={{ gap: "24px" }}>
              <Input
                type="text"
                value={
                  hideApiKey(connectedAppData?.access_token || "") ||
                  savedAccessToken
                }
                name="apiKey"
                variant="primary"
              />
              <Button
                themeRectangleButton
                title={"Update"}
                style={{
                  height: "40px",
                  marginTop: "0px",
                  width: "100px",
                }}
                onClick={handleOpenModal}
                processing={processing}
              />
            </div>
          </div>
          {stagesLoading || jobsLoading ? (
            <div
              className="d-flex align-items-center justify-content-center"
              style={{ height: "400px" }}
            >
              <div className="processing-indicator-wrapper medium-indicator">
                <div
                  className="processing-indicator primary"
                  style={{ width: "40px", height: "40px" }}
                />
              </div>
            </div>
          ) : (
            <>
              <div
                className="text-start d-flex flex-column stages-section"
                style={{ width: "580px", marginTop: "24px", gap: "24px" }}
              >
                <div className="d-flex" style={{ gap: "12px" }}>
                  <CustomSelect
                    title="Select Invite Stage"
                    className="select select-component"
                    style={{ width: "273px" }}
                    placeholder="Select a stage"
                    isRequired
                    options={(Array.isArray(stages) ? stages : []).map(
                      (stage) => ({
                        name: stage.name,
                        value: stage.name,
                      }),
                    )}
                    onSelection={(option) =>
                      handleUpdateHiringStage(
                        "assess_invitation_stage",
                        option.value,
                      )
                    }
                    selected={selectedStages.assess_invitation_stage || ""}
                    loading={false}
                  />
                  <CustomTooltip
                    customStyle={{
                      height: "20px",
                      width: "20px",
                      marginLeft: "5px",
                      display: "flex",
                      alignItems: "center",
                      marginTop: "37px",
                    }}
                    customPosition={{ bottom: "10px", left: "10px" }}
                    text={
                      "Choose the Workable stage on which you want the candidates to be invited."
                    }
                  >
                    <InfoIcon
                      style={{
                        width: "20px",
                        height: "20px",
                        stroke: "#808191",
                      }}
                    />
                  </CustomTooltip>
                </div>

                <div className="d-flex" style={{ gap: "12px" }}>
                  <CustomSelect
                    title="Select Shortlisted Stage"
                    className="select select-component"
                    style={{ width: "273px" }}
                    placeholder="Select a stage"
                    isRequired
                    options={(Array.isArray(stages) ? stages : []).map(
                      (stage) => ({
                        name: stage.name,
                        value: stage.name,
                      }),
                    )}
                    selected={selectedStages.post_assess_stage_shortlisted}
                    onSelection={(option) =>
                      handleUpdateHiringStage(
                        "post_assess_stage_shortlisted",
                        option.value,
                      )
                    }
                    loading={false}
                  />

                  <CustomTooltip
                    customStyle={{
                      height: "20px",
                      width: "20px",
                      marginLeft: "5px",
                      display: "flex",
                      alignItems: "center",
                      marginTop: "37px",
                    }}
                    customPosition={{ bottom: "10px", left: "10px" }}
                    text={
                      "Select where shortlisted Testfuse candidates should be placed in Workable."
                    }
                  >
                    <InfoIcon
                      style={{
                        width: "20px",
                        height: "20px",
                        stroke: "#808191",
                      }}
                    />
                  </CustomTooltip>
                </div>

                {/* Rejected Stage Section - Mutually Exclusive Options */}
                <div className="d-flex flex-column" style={{ gap: "8px" }}>
                  <label
                    className="body-2 dark-50"
                    style={{ paddingLeft: "24px" }}
                  >
                    Select Rejection Handling
                    <span className="danger-text">*</span>
                  </label>

                  {/* Option 1: Move to a specific stage */}
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "8px" }}
                  >
                    <input
                      type="radio"
                      id="useRejectedStage"
                      name="rejectionOption"
                      checked={!directlyReject}
                      onChange={() => {
                        setDirectlyReject(false);
                        // Set reject_on_assessment_failure to false when switching to stage selection
                        const updatedStages = {
                          ...selectedStages,
                          reject_on_assessment_failure: false,
                        };
                        setSelectedStages(updatedStages);
                        triggerIntegrationSettingAPI({
                          assessment_settings: updatedStages,
                        });
                      }}
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                        accentColor: "#6F2DBD",
                      }}
                    />
                    <label
                      htmlFor="useRejectedStage"
                      className="body-2 dark-100"
                      style={{ cursor: "pointer", marginBottom: 0 }}
                    >
                      Move Candidate To A Rejection Stage
                    </label>
                  </div>

                  {/* Show stage selector only when the first option is active */}
                  {!directlyReject && (
                    <div
                      className="d-flex flex-column"
                      style={{ gap: "6px", marginTop: 6 }}
                    >
                      {/* <label className="body-2 dark-50">Rejection Stage <span className="danger-text">*</span></label> */}
                      <div className="d-flex" style={{ gap: "12px" }}>
                        <CustomSelect
                          title=""
                          className="select select-component"
                          style={{ width: "273px" }}
                          placeholder="Select a stage"
                          options={(Array.isArray(stages) ? stages : []).map(
                            (stage) => ({
                              name: stage.name,
                              value: stage.name,
                            }),
                          )}
                          onSelection={(option) => {
                            handleUpdateHiringStage(
                              "post_assess_stage_rejected",
                              option.value,
                            );
                          }}
                          selected={
                            selectedStages.post_assess_stage_rejected || ""
                          }
                          loading={false}
                          disabled={false}
                        />
                        <CustomTooltip
                          customStyle={{
                            height: "20px",
                            width: "20px",
                            marginLeft: "5px",
                            display: "flex",
                            alignItems: "center",
                            marginTop: "10px",
                          }}
                          customPosition={{ bottom: "10px", left: "10px" }}
                          text={
                            "Select where rejected Testfuse candidates should be placed in Workable."
                          }
                        >
                          <InfoIcon
                            style={{
                              width: "20px",
                              height: "20px",
                              stroke: "#808191",
                            }}
                          />
                        </CustomTooltip>
                      </div>
                    </div>
                  )}

                  {/* Option 2: Directly disqualify */}
                  <div
                    className="d-flex align-items-center"
                    style={{ gap: "8px", marginTop: "8px" }}
                  >
                    <input
                      type="radio"
                      id="directlyReject"
                      name="rejectionOption"
                      checked={directlyReject}
                      onChange={() => {
                        setDirectlyReject(true);
                        // Set reject_on_assessment_failure to true and clear post_assess_stage_rejected
                        const updatedStages = {
                          ...selectedStages,
                          reject_on_assessment_failure: true,
                          post_assess_stage_rejected: "",
                        };
                        setSelectedStages(updatedStages);
                        triggerIntegrationSettingAPI({
                          assessment_settings: updatedStages,
                        });
                      }}
                      style={{
                        width: "16px",
                        height: "16px",
                        cursor: "pointer",
                        accentColor: "#6F2DBD",
                      }}
                    />
                    <label
                      htmlFor="directlyReject"
                      className="body-2 dark-100"
                      style={{ cursor: "pointer", marginBottom: 0 }}
                    >
                      Directly Disqualify Candidate
                    </label>
                    <CustomTooltip
                      customStyle={{
                        height: "20px",
                        width: "20px",
                        marginLeft: "5px",
                        display: "flex",
                        alignItems: "center",
                      }}
                      customPosition={{ bottom: "10px", left: "10px" }}
                      text={
                        "Candidates will be directly marked as disqualified in Workable instead of moving to a specific stage."
                      }
                    >
                      <InfoIcon
                        style={{
                          width: "20px",
                          height: "20px",
                          stroke: "#808191",
                        }}
                      />
                    </CustomTooltip>
                  </div>
                </div>
              </div>

              <div className="w-100 border"></div>

              <div className="d-flex justify-content-between align-items-center assessment-link-job-header">
                <div className="body-1 assessment-link-title">
                  Link {selectedIntegration?.name} Jobs to testfuse Assessments:
                </div>
                <div
                  className="table-search-container assessment-link-job-search"
                  style={{ width: "284px" }}
                >
                  <div className="table-search-input">
                    <Search height={"24px"} width={"24px"} />
                    <Input
                      inputStyle={{
                        background: "transparent",
                        border: "none",
                      }}
                      className={"w-100 p-0"}
                      placeholder={"Search"}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {filteredJobs.length > 0 ? (
                filteredJobs.map(
                  (job, idx) =>
                    idx < jobsPage * PAGE_SIZE && (
                      <div
                        key={job.id}
                        className=" jobs-section w-100 d-flex align-items-center justify-content-between "
                        style={{
                          background: "#F6F6F6",
                          padding: "12px 24px",
                          borderRadius: "24px",
                        }}
                      >
                        <div className="body-1 job-title">{job.name}</div>
                        <div
                          className="d-flex align-items-center job-assessmnet-link"
                          style={{ gap: "24px" }}
                        >
                          <Select
                            key={`job-${job.id}-${jobMappings?.find((m) => m.job_id === job.id)?.spec_id || "unlinked"}`}
                            readOnly
                            varrient="select-with-search"
                            style={{ width: "273px" }}
                            className="select question-select-responsive select-ideal-answer"
                            whiteBackground={true}
                            placeholder={
                              assessments
                                ? "Select Assessment"
                                : "Loading assessments..."
                            }
                            // innerClassName={error?.job_role_required ? "primary-input-error" : ""}
                            options={assessments
                              .slice(0, assessmentsPage * PAGE_SIZE)
                              .map((assessment) => ({
                                name: assessment.title,
                                value: assessment.id,
                              }))}
                            isRequired
                            innerStyle={{ width: "100%" }}
                            onAddOption={(option) => {
                              if (option) {
                                setJobRole([...jobRole, option]);
                              }
                            }}
                            onSelection={(option) => {
                              if (option?.value) {
                                handleUpdateJobRole(job.id, option.value);
                              }
                            }}
                            selected={
                              (assessments || [])
                                .map((a) => ({ name: a.title, value: a.id }))
                                .find(
                                  (a) =>
                                    a.value ===
                                    jobMappings?.find(
                                      (m) => m.job_id === job.id,
                                    )?.spec_id,
                                )?.value || null
                            }
                          />
                          <span>
                            <a
                              style={{
                                color: "#6F2DBD",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                              className="body-2"
                              onClick={() => handleUnlink(job.id)}
                            >
                              {unlinkProcess === job.id ? (
                                <div
                                  className="loader-container"
                                  style={{
                                    position: "relative",
                                    background: "none",
                                    boxShadow: "none",
                                    top: "0px",
                                    padding: "0px",
                                  }}
                                >
                                  <span className="processing-indicator-wrapper small-indicator">
                                    <span className="processing-indicator primary" />
                                  </span>
                                </div>
                              ) : jobMappings.some(
                                  (mapping) => mapping.job_id === job.id,
                                ) ? (
                                <img
                                  src={UnlinkIcon}
                                  alt=""
                                  style={{ height: "20px", width: "20px" }}
                                />
                              ) : (
                                <div
                                  style={{ height: "20px", width: "20px" }}
                                ></div>
                              )}
                            </a>
                          </span>
                        </div>
                      </div>
                    ),
                )
              ) : (
                <div>No jobs available</div>
              )}
              {jobsHasMore && jobs.length > 0 && (
                <div
                  onClick={() => {
                    fetchJobs(jobsPage + 1);
                    setJobsPage(jobsPage + 1);
                  }}
                  style={{
                    margin: "16px auto",
                    color: "black",
                    cursor: "pointer",
                  }}
                >
                  Load More Jobs
                </div>
              )}
            </>
          )}
        </div>
        <Modal
          // integration={selectedCardIntegration}
          open={modalOpen}
          onClose={handleCloseModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box>
            <div
              className="decline-assessment-modal-container"
              style={{ padding: "0px", borderRadius: "24px" }}
            >
              <div className="detail d-flex flex-column">
                <div style={{ padding: "24px" }}>
                  <span className="headline-small">Enter API KEY</span>
                  <div
                    className="w-100 d-flex flex-column"
                    style={{ gap: "12px" }}
                  >
                    <span className="body-2 dark-50 mt-3 mb-2">
                      <a
                        href="https://help.workable.com/hc/en-us/articles/115015785428-Generating-revoking-access-tokens-for-Workable-s-API"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#6F2DBD" }}
                      >
                        Click here
                      </a>{" "}
                      to find out how to generate your Workable API token.
                    </span>
                  </div>

                  <div style={{ marginTop: "16px" }}>
                    <Input
                      value={connectedAppData?.access_token}
                      // onChange={(e) => se(e.target.value)}
                      onChange={handleChange}
                      placeholder="Enter API Key"
                      className={apiError ? "error-input" : ""}
                    />

                    {apiError ? (
                      <div className="d-flex body-2 danger-text mt-2 align-items-center">
                        <div style={{ width: "30px" }}>
                          <ErrorIcon />
                        </div>
                        {apiError}
                      </div>
                    ) : null}
                  </div>

                  {/* Required Permissions Section */}
                  <div
                    className="d-flex flex-column"
                    style={{
                      background: "#F7F7FA",
                      padding: "16px",
                      borderRadius: "12px",
                      gap: "12px",
                      marginTop: "24px",
                    }}
                  >
                    <span
                      className="body-1 semibold dark-100"
                      style={{ marginBottom: "8px" }}
                    >
                      Required Permissions
                    </span>
                    <ul
                      className="body-2 dark-100"
                      style={{ paddingLeft: "20px", margin: 0 }}
                    >
                      <li style={{ marginBottom: "6px" }}>
                        📄 Read Jobs (r_jobs): Allows the app to read job
                        postings.
                      </li>
                      <li style={{ marginBottom: "6px" }}>
                        ✏️ Write Candidates (w_candidates): Allows creating or
                        updating candidate information.
                      </li>
                      <li style={{ marginBottom: "6px" }}>
                        💬 Write Comments (w_comments): Allows adding comments
                        or notes on candidates.
                      </li>
                      <li>
                        👀 Read Candidates (r_candidates): Allows reading
                        candidate details and profiles.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <hr className="hr" style={{ marginTop: "-10px" }} />

              <div className="w-100 d-flex flex-row justify-content-between">
                <div />
                <div
                  className="btn d-flex flex-row w-100"
                  style={{
                    justifyContent: "space-between",
                    padding: "24px",
                  }}
                >
                  <Button
                    btn="ragular-btn"
                    varrient="secondary-btn-v2"
                    title="Cancel"
                    style={{ width: "120px", height: "52px" }}
                    onClick={handleCloseModal}
                    // processing={processing}
                    rounded
                  />

                  <Button
                    btn="ragular-btn"
                    varrient="primary"
                    title="Submit"
                    style={{ width: "120px", height: "52px" }}
                    onClick={handleSave}
                    processing={processing}
                    rounded
                  />
                </div>
              </div>
            </div>
          </Box>
        </Modal>
      </div>
    </>
  );
};

export default IntegrationSetting;
